import { useCallback, useState } from 'react';

import type { DataVisibility } from '@/components/integrations/VisibilityChoiceModal';
import { redirectToProvider } from '@/lib/allauth';
import { fetchWithDefaults } from '@/lib/http/client';

const DEFAULT_ERROR_MESSAGE = "We couldn't start the connection. Try again.";

/**
 * `connect-for-team` rejects a viewer-seat member with a 403 carrying
 * `error_code: "viewer_read_only"` (apps/orgs/api/viewer_gate.py). That is not a
 * transient failure, so the default "Try again." copy sends the user into a loop
 * they cannot escape — it will fail identically every time. Keyed on the stable
 * error_code rather than the bare 403, which other causes also return.
 */
const VIEWER_READ_ONLY_CODE = 'viewer_read_only';
const VIEWER_ERROR_MESSAGE =
  "Viewers can't connect a mailbox. Ask a workspace admin to change your access.";

export function useOAuthConnect(
  orgId: string | undefined,
  provider: 'google' | 'microsoft'
) {
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connect = useCallback(
    async (dataVisibility: DataVisibility = 'private') => {
      if (!orgId) {
        setError('No team selected');
        return;
      }

      sessionStorage.setItem('oauth_return_url', window.location.pathname);

      setIsConnecting(true);
      setError(null);

      try {
        const response = await fetchWithDefaults(
          `/users/api/social-connections/connect-for-team/`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              org_id: orgId,
              provider,
              data_visibility: dataVisibility,
            }),
          }
        );

        if (!response.ok) {
          console.error(
            'OAuth connect failed:',
            response.status,
            response.statusText
          );
          // Read the body before falling through to the generic handler: a
          // viewer-seat rejection is permanent, and telling that user to "try
          // again" is a dead end. Returning (rather than throwing) keeps the
          // catch block from overwriting this more specific message.
          const body: unknown = await response.json().catch(() => null);
          const errorCode =
            typeof body === 'object' && body !== null
              ? (body as { error_code?: unknown }).error_code
              : undefined;
          if (errorCode === VIEWER_READ_ONLY_CODE) {
            setError(VIEWER_ERROR_MESSAGE);
            setIsConnecting(false);
            return;
          }
          throw new Error('connect_failed');
        }

        await response.json();

        await new Promise(resolve => setTimeout(resolve, 100));

        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- OAuth error response structure is complex and varies by error type
        (redirectToProvider as any)(
          provider,
          '/account/provider/callback',
          'connect'
        );
      } catch (err) {
        console.error('Error initiating connection:', err);
        setError(DEFAULT_ERROR_MESSAGE);
        setIsConnecting(false);
      }
    },
    [orgId, provider]
  );

  return { connect, isConnecting, error, setError };
}
